﻿import { useState, useRef, useEffect } from 'react';
import {
    Accordion, AccordionSummary, AccordionDetails, Typography, Box, Card, TextField,
    Checkbox, FormControlLabel, Radio, RadioGroup, Dialog, DialogTitle, DialogContent,
    Button, IconButton, Stack, Chip, CircularProgress, Grid,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import SignatureCanvas from 'react-signature-canvas';
import { useSelector } from 'react-redux';
import { Postrequestcall } from '../../request/Postrequest';
import {
    DELETE_CLIENT_INSPECTION_FORM_RESPONSES,
    FETCH_CLIENT_INSPECTION_SECTION_TEMPLATES_QUESTIONS,
    SAVE_CLIENT_INSPECTION_FORM_ANSWERS,
    SAVE_CLIENT_INSPECTION_FORM_RESPONSES,
    SAVE_CLIENT_INSPECTION_FORM_RESPONSES_FOR_FILE,
} from '../../constant/Apipath';
import dayjs from 'dayjs';
import { Fileuploadrequestcall } from '../../request/Fileuploadrequest';
import Loader from '../../common/Loader';
import JobList from './JobList';
import { BIGrey, BIRed } from '../../assets/helpers';
import SaveDraftModal from './SaveDraftModal';
import PictureViewer from '../../common/PictureViewer';
import { ClientEnglishFallback } from '../constant/ClientEnglishFallback';
import WorkIcon from '@mui/icons-material/Work';

/* ─── small helpers ──────────────────────────────────────────── */
const labelSx = {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: '#9499A8',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    mb: 0.5,
};

const fieldCard = {
    bgcolor: '#FAFBFC',
    border: '1px solid #ECEEF2',
    borderRadius: '14px',
    p: 2.5,
    mb: 0,
    transition: 'border-color 0.2s',
    overflow: 'hidden',
    minWidth: 0,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
};

const errorFieldCard = {
    ...fieldCard,
    borderLeft: '3px solid #EF4444',
    bgcolor: '#FFF8F8',
};

export const SectionViewModal = (props) => {
    const { open, onClose, selectedItem, getList } = props;
    const [expanded, setExpanded] = useState([]);
    const [questionList, setQuestionList] = useState([]);
    const [answers, setAnswers] = useState({});
    const [openJobList, setOpenJobList] = useState(false);
    const [isSectionQuestionListLoading, setIsQuestionListLoading] = useState(false);
    const [activeJobQuestionId, setActiveJobQuestionId] = useState(null);
    const [deletedQuestions, setDeletedQuestions] = useState([]);
    const [originalAnswers, setOriginalAnswers] = useState({});
    const [isInspectionSaving, setIsInspectionSaving] = useState(false);
    const [openSaveDraftModal, setOpenSaveDraftModal] = useState(false);
    const [signatureDrawnMap, setSignatureDrawnMap] = useState({});
    const [pictureView, setPictureView] = useState(false);
    const [selectedImages, setSelectedImages] = useState([]);
    const [viewerIndex, setViewerIndex] = useState(0);
    const [errors, setErrors] = useState({});
    const [modifiedQuestionIds, setModifiedQuestionIds] = useState(new Set());
    const [errorMessage, setErrorMessage] = useState(null);

    const { loginData, selectedClientData, multiLanguageData } = useSelector((state) => state?.main);
    const sigCanvasRef = useRef({});
    const fileInputRef = useRef({});
    const questionRefs = useRef({});

    const accentColor = selectedClientData?.AccentColor || '#4F6EF7';

    const LANGUAGE = ClientEnglishFallback.ClientInspections(multiLanguageData?.ClientInspections || {});
    const {
        TEXT_NO_ANSWER_PROVIDED, TEXT_PLEASE_TYPE_YOUR_ANS, TEXT_SELECT_A_DATE,
        TEXT_SELECT_A_START_DATE, TEXT_SELECT_END_DATE, TEXT_AFTER_ADDING_YOUR_SIGNATURE,
        TEXT_SAVE, TEXT_CLEAR, BUTTON_RESET, TEXT_SELECT_JOBS, TEXT_UNSUPPORTED_FIELD_TYPE,
        TEXT_NO_QUESTIONS_AVAILABLE, TEXT_NO_SEC_NO_QUE_AVAILABLE, TEXT_SUBMIT,
    } = LANGUAGE;

    /* ─── helpers ─────────────────────────────────────────────── */
    const isEmptyValue = (val, answerType) => {
        if (val == null) return true;
        if (Array.isArray(val)) return val.length === 0;
        if (answerType === 'DATE' || answerType === 'TIME') return !val || !dayjs(val).isValid();
        if (answerType === 'DATE_RANGE') {
            if (typeof val === 'object' && !Array.isArray(val)) return !(val.startDate && val.endDate);
            return true;
        }
        if (answerType === 'SIGN') return !val || val === '';
        if (typeof val === 'string') return val.trim() === '';
        return false;
    };

    const updateAnswer = (questionId, newValue) => {
        setAnswers((prev) => ({ ...prev, [questionId]: { ...prev[questionId], value: newValue } }));
        setModifiedQuestionIds((prev) => new Set(prev).add(questionId));
        if (errors[questionId]) {
            setErrors((prev) => { const next = { ...prev }; delete next[questionId]; return next; });
        }
    };

    const validateAnswers = () => {
        const allQuestions = [];
        questionList.forEach((item) => {
            if (item.type === 'section' && Array.isArray(item.questions)) allQuestions.push(...item.questions);
            else if (item.questionId) allQuestions.push(item);
        });
        const newErrors = {};
        for (const q of allQuestions) {
            if (!q.isResponseRequired) continue;
            if (isEmptyValue(answers[q.questionId]?.value, q.answerType)) newErrors[q.questionId] = true;
        }
        return newErrors;
    };

    const handleAccordionChange = (index) => (event, isExpanded) => {
        if (!selectedItem?.isSubmit) {
            setExpanded((prev) => isExpanded ? [...prev, index] : prev.filter((i) => i !== index));
        }
    };

    useEffect(() => {
        if (questionList.length > 0) setExpanded(questionList.map((_, i) => i));
    }, [selectedItem?.isSubmit, questionList]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            questionList.forEach((section, sectionIndex) => {
                if (section.questions?.some((q) => errors[q.questionId])) {
                    if (!expanded.includes(sectionIndex)) setExpanded((prev) => [...prev, sectionIndex]);
                }
            });
        }
    }, [errors]);

    const handleImageClick = (images, clickedIndex) => {
        const imageUrls = images?.map((img) => img?.file ? URL.createObjectURL(img.file) : img?.url);
        setSelectedImages(imageUrls);
        setViewerIndex(clickedIndex);
        setPictureView(true);
    };

    const handleFileUpload = (qId, files, allowMultiple = true) => {
        const currentImages = answers[qId]?.value || [];
        const newImages = Array.from(files).map((file, i) => ({
            id: `${i}-${Date.now().toString(36) + Math.random().toString(36).substring(2, 11)}`,
            file, url: URL.createObjectURL(file), isNew: true, name: `image_${i}`, mimeType: file.type,
        }));
        updateAnswer(qId, allowMultiple ? [...currentImages, ...newImages] : newImages.slice(0, 1));
    };

    const handleImageRemove = (qId, imageIndex) => {
        const currentImages = answers[qId]?.value || [];
        const imageToRemove = currentImages[imageIndex];
        if (!imageToRemove?.isNew && imageToRemove?.id) {
            setDeletedQuestions((prev) => prev.includes(imageToRemove.id) ? prev : [...prev, imageToRemove.id]);
        }
        updateAnswer(qId, currentImages.filter((_, index) => index !== imageIndex));
    };

    const getInspectionsQuestionList = async () => {
        setIsQuestionListLoading(true);
        try {
            const requestPayload = {
                FormId: selectedItem?.formId,
                FormAnswerId: selectedItem?.id,
                EmployeeNo: selectedClientData?.ClientEmployeeId,
            };
            const res = await Postrequestcall(
                FETCH_CLIENT_INSPECTION_SECTION_TEMPLATES_QUESTIONS,
                requestPayload,
                loginData?.accessToken
            );
            if (res?.data?.data) setQuestionList(res?.data?.data);
        } catch (error) {
            console.log('getInspectionsQuestionList error', error);
            setErrorMessage('Failed to load questions. Please try again.');
        } finally {
            setIsQuestionListLoading(false);
        }
    };

    useEffect(() => {
        if (open && questionList.length > 0) {
            const initial = {};
            setErrors({});
            setModifiedQuestionIds(new Set());
            questionList.forEach((item) => {
                const questions = item.type === 'section' ? item.questions : item.questionId ? [item] : [];
                questions.forEach((q) => {
                    let value;
                    switch (q.answerType) {
                        case 'CHECKLIST':
                            if (q.answer) {
                                try {
                                    const opts = JSON.parse(q.jsonAnswerOptions || '{}');
                                    if (q.answer.trim().startsWith('{')) {
                                        const parsed = JSON.parse(q.answer);
                                        value = Object.entries(parsed).map(([key, val]) => ({ id: key, value: typeof val === 'string' ? val.trim() : String(val) }));
                                    } else {
                                        const rawVals = q.answer.split(',').map((s) => s.trim());
                                        value = Object.entries(opts).filter(([key, val]) => rawVals.includes(val.trim())).map(([key, val]) => ({ id: key, value: val.trim() }));
                                    }
                                } catch { value = []; }
                            } else { value = []; }
                            break;
                        case 'MULTI_CHOICE': case 'SHORT_ANS': case 'LONG_ANS': case 'SIGN': value = q.answer || ''; break;
                        case 'DATE': case 'TIME': value = q.answer ? dayjs(q.answer) : null; break;
                        case 'DATE_RANGE':
                            if (q.answer) {
                                try {
                                    const parsed = JSON.parse(q.answer);
                                    value = { startDate: parsed.startDate ? dayjs(parsed.startDate) : null, endDate: parsed.endDate ? dayjs(parsed.endDate) : null };
                                } catch { value = { startDate: null, endDate: null }; }
                            } else { value = { startDate: null, endDate: null }; }
                            break;
                        case 'IMG':
                            if (q.answer) {
                                try {
                                    const parsed = typeof q.answer === 'string' ? JSON.parse(q.answer) : q.answer;
                                    value = parsed?.map((img) => ({ id: img.AnswerId, url: img.PictureUrl, isNew: false }));
                                } catch { value = []; }
                            } else { value = []; }
                            break;
                        case 'JOB':
                            if (q.answer) {
                                try {
                                    const parsedAnswer = JSON.parse(q.answer);
                                    value = Array.isArray(parsedAnswer) ? parsedAnswer.map((job) => ({ id: job.Id, label: job.Name, fullLabel: `${job.JobCode} - ${job.Name}` })) : [];
                                } catch { value = []; }
                            } else { value = []; }
                            break;
                        default: value = q.answer || '';
                    }
                    initial[q.questionId] = { id: q.answerId || 0, value };
                });
            });
            setAnswers(initial);
            setOriginalAnswers(initial);
        }
    }, [open, questionList]);

    const hasUnsavedChanges = () => !selectedItem?.isSubmit && modifiedQuestionIds.size > 0;

    const base64ToBlob = (base64, mime) => {
        const byteChars = atob(base64.split(',')[1]);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
        return new Blob([new Uint8Array(byteNumbers)], { type: mime });
    };

    const saveInspectionFormAnswer = async (id, isSubmitted) => {
        try {
            const obj = {
                UserId: loginData?.userInformation?.UserId, ReturnRecordId: true, ReturnRecordError: true,
                SaveList: [{ Id: id || 0, FormId: selectedItem?.formId, ClientId: selectedClientData?.ClientId, EmployeeId: selectedClientData?.ClientEmployeeId, ModifiedBy: loginData?.userInformation?.UserId, isSubmit: isSubmitted || false }],
            };
            const formAnswerRes = await Postrequestcall(SAVE_CLIENT_INSPECTION_FORM_ANSWERS, obj, loginData?.accessToken);
            return Number(formAnswerRes?.data.data[0]?.objData?.id || 0);
        } catch (error) {
            console.error('SAVE_CLIENT_INSPECTION_FORM_ANSWERS [ERROR]: ', error);
            throw error;
        }
    };

    const handleSaveClick = async (calledFrom) => {
        const isSubmitted = calledFrom === 'Submit';
        if (isSubmitted) {
            const validationErrors = validateAnswers();
            setErrors(validationErrors);
            if (Object.keys(validationErrors).length > 0) {
                const allQuestions = [];
                questionList.forEach((item) => {
                    if (item.type === 'section' && Array.isArray(item.questions)) allQuestions.push(...item.questions);
                    else if (item.questionId) allQuestions.push(item);
                });
                for (const q of allQuestions) {
                    if (validationErrors[q.questionId]) {
                        setTimeout(() => { questionRefs.current[q.questionId]?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 300);
                        break;
                    }
                }
                return;
            }
        } else {
            setErrors({});
        }

        setIsInspectionSaving(true);
        try {
            let formAnswerId = selectedItem?.id;
            if (!formAnswerId || formAnswerId === 0) {
                formAnswerId = await saveInspectionFormAnswer(0, isSubmitted);
            } else if (isSubmitted) {
                await saveInspectionFormAnswer(formAnswerId, true);
            }

            const allQuestions = [];
            questionList.forEach((item) => {
                if (item.type === 'section' && Array.isArray(item.questions)) allQuestions.push(...item.questions);
                else if (item.questionId) allQuestions.push(item);
            });

            const filteredQuestions = allQuestions.filter((q) => !(q?.answerType === 'IMG' || q?.answerType === 'SIGN'));
            const saveList = filteredQuestions
                .filter((q) => modifiedQuestionIds.has(q.questionId))
                .map((q) => {
                    let formattedAnswer = answers[q?.questionId]?.value;
                    if (q?.answerType === 'CHECKLIST') {
                        const selectedMap = (formattedAnswer || []).reduce((acc, curr) => { if (curr?.id != null) acc[curr.id] = (curr.value ?? '') + ' '; return acc; }, {});
                        formattedAnswer = JSON.stringify(selectedMap);
                    } else if (q?.answerType === 'JOB') {
                        formattedAnswer = (formattedAnswer || []).map((job) => job?.id).join(',');
                    } else if (q?.answerType === 'DATE' || q?.answerType === 'TIME') {
                        formattedAnswer = formattedAnswer?.isValid() ? formattedAnswer.toISOString() : '';
                    } else if (q?.answerType === 'DATE_RANGE') {
                        formattedAnswer = JSON.stringify({ startDate: formattedAnswer?.startDate?.isValid() ? formattedAnswer.startDate.toISOString() : '', endDate: formattedAnswer?.endDate?.isValid() ? formattedAnswer.endDate.toISOString() : '' });
                    }
                    return {
                        Id: answers[q?.questionId]?.id || 0, QuestionId: q?.questionId,
                        EmployeeNo: selectedClientData?.ClientEmployeeId, AnswerTypeId: q?.answerTypeId,
                        Answer: formattedAnswer, ClientId: selectedClientData?.ClientId,
                        ModifiedBy: loginData?.userInformation?.UserId, FormAnswerId: formAnswerId || selectedItem?.id,
                    };
                })
                .filter((q) => q.Answer !== '' && q.Answer !== null && q.Answer !== undefined && q.Answer !== '[]' && q.Answer !== '{}');

            if (saveList.length > 0) {
                await Postrequestcall(SAVE_CLIENT_INSPECTION_FORM_RESPONSES, { UserId: loginData?.userInformation?.UserId, ReturnRecordId: true, ReturnRecordError: true, SaveList: saveList }, loginData?.accessToken);
            }

            const fileAnswers = allQuestions.filter((q) => {
                if (q?.answerType !== 'IMG' && q?.answerType !== 'SIGN') return false;
                const ansVal = answers[q?.questionId]?.value;
                if (!ansVal) return false;
                if (q?.answerType === 'IMG') return Array.isArray(ansVal) && ansVal.some((img) => img.isNew);
                if (q?.answerType === 'SIGN') return typeof ansVal === 'string' && ansVal.startsWith('data:image');
                return false;
            });

            if (fileAnswers.length > 0) {
                for (const q of fileAnswers) {
                    const ansObj = answers[q.questionId];
                    const rawVal = ansObj.value || [];
                    const images = Array.isArray(rawVal) ? rawVal : [rawVal];
                    for (const img of images) {
                        if (img.isNew && img.file) {
                            const formdata = new FormData();
                            formdata.append('Id', ansObj?.id || 0);
                            formdata.append('QuestionId', q.questionId);
                            formdata.append('EmployeeNo', selectedClientData?.ClientEmployeeId);
                            formdata.append('AnswerTypeId', q.answerTypeId);
                            formdata.append('Answer', 0);
                            formdata.append('ClientId', selectedClientData?.ClientId);
                            formdata.append('ModifiedBy', loginData?.userInformation?.UserId);
                            formdata.append('FormAnswerId', formAnswerId || selectedItem?.id);
                            formdata.append('FileName', img.file);
                            await Fileuploadrequestcall(SAVE_CLIENT_INSPECTION_FORM_RESPONSES_FOR_FILE, formdata, loginData?.accessToken);
                        }
                        if (typeof img === 'string' && img.startsWith('data:image')) {
                            const mime = img.split(',')[0].split(':')[1].split(';')[0];
                            const blob = base64ToBlob(img, mime);
                            const formdata = new FormData();
                            formdata.append('Id', ansObj?.id || 0);
                            formdata.append('QuestionId', q.questionId);
                            formdata.append('EmployeeNo', selectedClientData?.ClientEmployeeId);
                            formdata.append('AnswerTypeId', q.answerTypeId);
                            formdata.append('Answer', 0);
                            formdata.append('ClientId', selectedClientData?.ClientId);
                            formdata.append('ModifiedBy', loginData?.userInformation?.UserId);
                            formdata.append('FormAnswerId', formAnswerId || selectedItem?.id);
                            formdata.append('FileName', blob, 'signature.png');
                            await Fileuploadrequestcall(SAVE_CLIENT_INSPECTION_FORM_RESPONSES_FOR_FILE, formdata, loginData?.accessToken);
                        }
                    }
                }
            }

            if (deletedQuestions.length > 0) await deleteAnswerId(deletedQuestions);
            setIsInspectionSaving(false);
            onClose();
            getList();
        } catch (error) {
            console.error('Error saving answers: ', error);
            setErrorMessage('Something went wrong. Please try again later.');
            setIsInspectionSaving(false);
        }
    };

    const handleSaveSignature = (qId) => {
        const canvas = sigCanvasRef.current[qId];
        if (canvas && !canvas.isEmpty()) {
            const sigValue = canvas.getCanvas().toDataURL('image/png');
            const wasReplacingOriginal = !!answers[qId]?.value && !!answers[qId]?.id;
            if (wasReplacingOriginal) setDeletedQuestions((prev) => prev.includes(answers[qId].id) ? prev : [...prev, answers[qId].id]);
            updateAnswer(qId, sigValue);
            setSignatureDrawnMap((prev) => ({ ...prev, [qId]: true }));
        }
    };

    const handleResetSignature = (qId) => {
        const canvas = sigCanvasRef.current[qId];
        if (canvas) canvas.clear();
        if (answers[qId]?.id && answers[qId]?.value) setDeletedQuestions((prev) => prev.includes(answers[qId].id) ? prev : [...prev, answers[qId].id]);
        updateAnswer(qId, '');
        setSignatureDrawnMap((prev) => ({ ...prev, [qId]: false }));
    };

    const deleteAnswerId = async (deleteAnswerIdArr) => {
        if (deleteAnswerIdArr.length > 0) {
            const deletePayload = {
                UserId: loginData?.userInformation?.UserId, ReturnRecordId: false, ReturnRecordError: true,
                SoftDelete: true, DeleteList: deleteAnswerIdArr.map((id) => ({ Id: id })),
            };
            await Postrequestcall(DELETE_CLIENT_INSPECTION_FORM_RESPONSES, deletePayload, loginData?.accessToken);
            setDeletedQuestions([]);
        }
    };

    /* ─── shared field styles ─────────────────────────────────── */
    const sharedTextFieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            bgcolor: '#fff',
            fontSize: '0.875rem',
            '& fieldset': { borderColor: '#E5E7EB' },
            '&:hover fieldset': { borderColor: '#D1D5DB' },
            '&.Mui-focused fieldset': { borderColor: accentColor, borderWidth: 1.5 },
        },
    };

    /* ─── renderInputField ────────────────────────────────────── */
    const renderInputField = (q) => {
        const value = answers[q.questionId]?.value;
        const isDisabled = selectedItem?.isSubmit;

        if (isDisabled && isEmptyValue(value, q.answerType)) {
            return (
                <Typography
                    sx={{ color: '#9499A8', fontSize: '0.875rem', fontStyle: 'italic' }}
                >
                    {TEXT_NO_ANSWER_PROVIDED}
                </Typography>
            );
        }

        switch (q.answerType) {
            case 'SHORT_ANS':
                return (
                    <TextField fullWidth size="small" placeholder={isDisabled ? '' : TEXT_PLEASE_TYPE_YOUR_ANS}
                        value={value || ''} disabled={isDisabled}
                        onChange={(e) => !isDisabled && updateAnswer(q.questionId, e.target.value)}
                        sx={sharedTextFieldSx}
                    />
                );
            case 'LONG_ANS':
                return (
                    <TextField fullWidth multiline minRows={3} maxRows={6}
                        placeholder={isDisabled ? '' : TEXT_PLEASE_TYPE_YOUR_ANS}
                        value={value || ''} disabled={isDisabled}
                        onChange={(e) => !isDisabled && updateAnswer(q.questionId, e.target.value)}
                        sx={sharedTextFieldSx}
                    />
                );
            case 'DATE':
                return (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker value={value || null} disabled={isDisabled}
                            onChange={(newVal) => !isDisabled && updateAnswer(q.questionId, newVal)}
                            sx={{ ...sharedTextFieldSx, '& .MuiSvgIcon-root': { color: isDisabled ? 'rgba(0,0,0,0.26)' : accentColor } }}
                            slotProps={{ textField: { fullWidth: true, size: 'small', disabled: isDisabled, sx: sharedTextFieldSx }, field: { placeholder: TEXT_SELECT_A_DATE, onKeyDown: (e) => { if (e.key === 'Backspace' || e.key === 'Delete') e.preventDefault(); } } }}
                        />
                    </LocalizationProvider>
                );
            case 'TIME':
                return (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker value={value || null} disabled={isDisabled}
                            onChange={(newVal) => !isDisabled && updateAnswer(q.questionId, newVal)}
                            sx={{ ...sharedTextFieldSx, '& .MuiSvgIcon-root': { color: isDisabled ? 'rgba(0,0,0,0.26)' : accentColor } }}
                            slotProps={{ textField: { fullWidth: true, size: 'small', disabled: isDisabled, sx: sharedTextFieldSx } }}
                        />
                    </LocalizationProvider>
                );
            case 'DATE_RANGE':
                return (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Stack sx={{ gap: 2 }} direction={{ xs: 'column', sm: 'row' }} >
                            <Box flex={1}>
                                <DatePicker label={value?.startDate ? null : TEXT_SELECT_A_START_DATE}
                                    value={value?.startDate || null} disabled={isDisabled} maxDate={value?.endDate}
                                    onChange={(newVal) => !isDisabled && updateAnswer(q.questionId, { ...value, startDate: newVal })}
                                    slotProps={{ textField: { size: 'small', fullWidth: true, disabled: isDisabled, sx: sharedTextFieldSx } }}
                                />
                            </Box>
                            <Box flex={1}>
                                <DatePicker label={value?.endDate ? null : TEXT_SELECT_END_DATE}
                                    value={value?.endDate || null} disabled={isDisabled} minDate={value?.startDate}
                                    onChange={(newVal) => !isDisabled && updateAnswer(q.questionId, { ...value, endDate: newVal })}
                                    slotProps={{ textField: { size: 'small', fullWidth: true, disabled: isDisabled, sx: sharedTextFieldSx } }}
                                />
                            </Box>
                        </Stack>
                    </LocalizationProvider>
                );
            case 'CHECKLIST': {
                const opts = JSON.parse(q.jsonAnswerOptions || '{}');
                const selectedIds = (value || []).map((v) => v?.id);
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {Object.entries(opts).map(([key, val]) => {
                            const trimmedVal = typeof val === 'string' ? val.trim() : String(val);
                            const isChecked = selectedIds.includes(key) || false;
                            return (
                                <FormControlLabel key={key}
                                    control={
                                        <Checkbox size="small" checked={isChecked} disabled={isDisabled}
                                            onChange={(e) => {
                                                if (!isDisabled) {
                                                    const prevVals = value || [];
                                                    const newVals = e.target.checked
                                                        ? [...prevVals, { id: key, value: trimmedVal }]
                                                        : prevVals.filter((v) => v?.id !== key);
                                                    updateAnswer(q.questionId, newVals);
                                                }
                                            }}
                                            sx={{ '& .MuiSvgIcon-root': { color: isDisabled ? 'rgba(0,0,0,0.26)' : accentColor } }}
                                        />
                                    }
                                    label={<Typography sx={{ fontSize: '0.875rem', color: '#374151' }}>{trimmedVal}</Typography>}
                                    sx={{
                                        m: 0, px: 1, py: 0.5, borderRadius: '8px',
                                        bgcolor: isChecked ? `${accentColor}0C` : 'transparent',
                                        transition: 'background-color 0.15s',
                                    }}
                                />
                            );
                        })}
                    </Box>
                );
            }
            case 'MULTI_CHOICE': {
                const opts = JSON.parse(q.jsonAnswerOptions || '{}');
                const currentValue = value || '';
                const handleRadioClick = (optionValue) => (event) => {
                    if (isDisabled) return;
                    if (currentValue === optionValue) { event.preventDefault(); updateAnswer(q.questionId, ''); }
                };
                return (
                    <RadioGroup value={currentValue}
                        onChange={!isDisabled ? (e) => updateAnswer(q.questionId, e.target.value) : undefined}
                        sx={{ gap: 0.5 }}
                    >
                        {Object.entries(opts).map(([key, val]) => (
                            <FormControlLabel key={key} value={val} label={<Typography sx={{ fontSize: '0.875rem', color: '#374151' }}>{val}</Typography>}
                                disabled={isDisabled}
                                control={
                                    <Radio size="small" disabled={isDisabled} onClick={handleRadioClick(val)}
                                        sx={{ '& .MuiSvgIcon-root': { color: isDisabled ? 'rgba(0,0,0,0.26)' : accentColor } }}
                                    />
                                }
                                sx={{
                                    m: 0, px: 1, py: 0.5, borderRadius: '8px',
                                    bgcolor: currentValue === val ? `${accentColor}0C` : 'transparent',
                                    transition: 'background-color 0.15s',
                                }}
                            />
                        ))}
                    </RadioGroup>
                );
            }
            case 'IMG':
                return (
                    <Box>
                        <input
                            ref={(el) => fileInputRef.current[q.questionId] = el}
                            type="file" accept="image/*"
                            multiple={q.areMultipleImagesAllowed}
                            style={{ display: 'none' }}
                            onChange={(e) => {
                                if (e.target.files.length > 0) {
                                    handleFileUpload(q.questionId, e.target.files, q.areMultipleImagesAllowed);
                                    e.target.value = '';
                                }
                            }}
                        />
                        <Grid container spacing={1.2}>
                            {Array.isArray(value) && value.length > 0 && value.map((img, i) => (
                                <Grid item key={i}>
                                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                        <img
                                            src={img.file ? URL.createObjectURL(img.file) : img.url}
                                            alt={`uploaded-${i}`} width={76} height={76}
                                            style={{ borderRadius: 10, cursor: 'pointer', objectFit: 'cover', border: '1px solid #ECEEF2' }}
                                            onClick={() => handleImageClick(value, i)}
                                        />
                                        {!isDisabled && (
                                            <IconButton size="small"
                                                sx={{ position: 'absolute', top: 3, right: 3, p: 0.3, bgcolor: 'white', borderRadius: '50%', boxShadow: '0 1px 4px rgba(0,0,0,0.15)', '&:hover': { bgcolor: '#FEE2E2' } }}
                                                onClick={() => handleImageRemove(q.questionId, i)}
                                            >
                                                <CloseIcon sx={{ fontSize: 11, color: '#EF4444' }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Grid>
                            ))}
                            {!isDisabled && (
                                <>
                                    {q.areMultipleImagesAllowed && (
                                        <Grid item>
                                            <Box onClick={() => fileInputRef.current[q.questionId]?.click()}
                                                sx={{ width: 76, height: 76, border: `2px dashed ${accentColor}55`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', bgcolor: `${accentColor}06`, transition: 'all 0.15s', '&:hover': { borderColor: accentColor, bgcolor: `${accentColor}12` } }}
                                            >
                                                <AddIcon sx={{ fontSize: 28, color: accentColor }} />
                                            </Box>
                                        </Grid>
                                    )}
                                    {!q.areMultipleImagesAllowed && (!Array.isArray(value) || value.length === 0) && (
                                        <Grid item>
                                            <Box onClick={() => fileInputRef.current[q.questionId]?.click()}
                                                sx={{ width: 76, height: 76, border: `2px dashed ${accentColor}55`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', bgcolor: `${accentColor}06`, transition: 'all 0.15s', '&:hover': { borderColor: accentColor, bgcolor: `${accentColor}12` } }}
                                            >
                                                <AddIcon sx={{ fontSize: 28, color: accentColor }} />
                                            </Box>
                                        </Grid>
                                    )}
                                </>
                            )}
                        </Grid>
                    </Box>
                );
            case 'SIGN':
                return (
                    <Box>
                        {!isDisabled && !value && (
                            <Box sx={{ border: `1.5px solid #ECEEF2`, borderRadius: '12px', p: 2, bgcolor: '#FAFBFC' }}>
                                <Typography variant="body2" sx={{ color: '#9499A8', mb: 1.5, fontSize: '0.8rem' }}>
                                    {TEXT_AFTER_ADDING_YOUR_SIGNATURE}
                                </Typography>
                                <Box sx={{ border: `1px dashed ${accentColor}55`, borderRadius: '8px', bgcolor: '#fff', overflow: 'hidden' }}>
                                    <SignatureCanvas
                                        ref={(ref) => sigCanvasRef.current[q.questionId] = ref}
                                        canvasProps={{ width: 775, height: 140, className: 'signature-canvas' }}
                                        onEnd={() => setSignatureDrawnMap((prev) => ({ ...prev, [q.questionId]: true }))}
                                    />
                                </Box>
                                <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                                    <Button size="small" variant="contained"
                                        disabled={!signatureDrawnMap[q.questionId]}
                                        onClick={() => handleSaveSignature(q.questionId)}
                                        sx={{ bgcolor: accentColor, borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', px: 2, '&:hover': { bgcolor: accentColor, opacity: 0.9 } }}
                                    >
                                        {TEXT_SAVE}
                                    </Button>
                                    <Button size="small" variant="outlined"
                                        onClick={() => handleResetSignature(q.questionId)}
                                        sx={{ borderColor: '#E5E7EB', color: '#6B7280', borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', px: 2, '&:hover': { borderColor: '#D1D5DB', bgcolor: '#F9FAFB' } }}
                                    >
                                        {TEXT_CLEAR}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                        {value && (
                            <Box>
                                <Box sx={{ display: 'inline-block', border: '1px solid #ECEEF2', borderRadius: '10px', p: 1, bgcolor: '#fff' }}>
                                    <img src={value} alt="signature" style={{ maxHeight: 110, maxWidth: '100%', display: 'block', borderRadius: 4 }} />
                                </Box>
                                {!isDisabled && (
                                    <Box sx={{ mt: 1.5 }}>
                                        <Button size="small" variant="outlined"
                                            onClick={() => handleResetSignature(q.questionId)}
                                            sx={{ borderColor: '#E5E7EB', color: '#6B7280', borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', px: 2, '&:hover': { borderColor: '#D1D5DB', bgcolor: '#F9FAFB' } }}
                                        >
                                            {BUTTON_RESET}
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                );
            case 'JOB':
                return (
                    <Box>
                        {!isDisabled && (
                            <Button variant="outlined"
                                startIcon={<WorkIcon sx={{ fontSize: 16 }} />}
                                onClick={() => { setActiveJobQuestionId(q.questionId); setOpenJobList(true); }}
                                sx={{ borderColor: `${accentColor}66`, color: accentColor, borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', px: 2, py: 0.8, '&:hover': { borderColor: accentColor, bgcolor: `${accentColor}0C` } }}
                            >
                                {TEXT_SELECT_JOBS}
                            </Button>
                        )}
                        {Array.isArray(value) && value.length > 0 && (
                            <Box sx={{ display: 'flex', mt: 1.5, flexWrap: 'wrap', gap: 0.8 }}>
                                {value.map((job) => (
                                    <Chip key={job.id} label={job.fullLabel || job.label} size="small"
                                        onDelete={!isDisabled ? () => updateAnswer(q.questionId, value.filter((j) => j.id !== job.id)) : undefined}
                                        sx={{ fontSize: '0.78rem', fontWeight: 500, bgcolor: `${accentColor}10`, color: '#374151', border: `1px solid ${accentColor}30`, borderRadius: '8px', '& .MuiChip-deleteIcon': { color: accentColor, fontSize: 16 } }}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                );
            default:
                return <Typography sx={{ color: '#9499A8', fontSize: '0.875rem' }}>{TEXT_UNSUPPORTED_FIELD_TYPE}: {q.answerType}</Typography>;
        }
    };

    useEffect(() => {
        if (open) getInspectionsQuestionList();
    }, [open]);

    /* ─── render question block ───────────────────────────────── */
    const renderQuestion = (question, withDivider = false) => (
        <Box
            key={question.questionId}
            ref={(el) => questionRefs.current[question.questionId] = el}
            sx={errors[question.questionId] ? errorFieldCard : fieldCard}
        >
            <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1A1D23', mb: 0.5, lineHeight: 1.4, wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}
            >
                {question.question}
                {question.isResponseRequired && (
                    <Typography component="span" sx={{ color: '#EF4444', ml: 0.4, fontWeight: 700 }}>*</Typography>
                )}
            </Typography>
            {question.questionDescription && (
                <Typography variant="body2" sx={{ color: '#9499A8', fontSize: '0.78rem', mb: 1.5, wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                    {question.questionDescription}
                </Typography>
            )}
            {errors[question.questionId] && (
                <Typography sx={{ color: '#EF4444', fontSize: '0.75rem', fontWeight: 600, mb: 1 }}>
                    This field is required
                </Typography>
            )}
            <Box sx={{ mt: question.questionDescription ? 0 : 1.5 }}>
                {renderInputField(question)}
            </Box>
        </Box>
    );

    /* ─── JSX ─────────────────────────────────────────────────── */
    return (
        <>
            <Dialog
                open={open}
                onClose={() => hasUnsavedChanges() ? setOpenSaveDraftModal(true) : onClose()}
                maxWidth="md"
                fullWidth
                sx={{
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        boxShadow: '0 24px 60px rgba(0,0,0,0.14)',
                    },
                }}
            >
                {/* Title bar */}
                <DialogTitle
                    sx={{
                        px: 3,
                        pt: 2.5,
                        pb: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottom: '1px solid #F0F2F5',
                    }}
                >
                    <Box>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#1A1D23', letterSpacing: '-0.02em' }}
                        >
                            {selectedItem?.formName}
                        </Typography>
                        {selectedItem?.isSubmit && (
                            <Chip
                                label="Submitted"
                                size="small"
                                sx={{ bgcolor: '#EDFAF3', color: '#22A769', fontWeight: 600, fontSize: '0.7rem', height: 20, mt: 0.5, borderRadius: '6px', border: '1px solid #9AE6B4' }}
                            />
                        )}
                    </Box>
                    <IconButton
                        disabled={isInspectionSaving}
                        onClick={() => hasUnsavedChanges() ? setOpenSaveDraftModal(true) : onClose()}
                        size="small"
                        sx={{ bgcolor: '#F5F6FA', color: '#6B7280', borderRadius: '10px', '&:hover': { bgcolor: '#ECEEF2', color: '#1A1D23' } }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                {/* Body */}
                <DialogContent dividers sx={{ p: 0, bgcolor: '#F7F8FA', border: 'none' }}>
                    {isSectionQuestionListLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                            <Loader />
                        </Box>
                    ) : questionList?.length > 0 ? (
                        <Stack spacing={1} sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
                            {questionList.map((item, itemIndex) => {
                                /* standalone question */
                                if (item.type !== 'section' && item.questionId) {
                                    return renderQuestion(item);
                                }

                                /* section */
                                const section = item;
                                const hasHeader = section?.sectionId !== 0 || section?.sectionName !== '';

                                return (
                                    <Card
                                        key={section.sectionId || `sec-${itemIndex}`}
                                        variant="outlined"
                                        sx={{ borderRadius: '12px',  border: '1px solid #ECEEF2', overflow: 'hidden', bgcolor: '#fff' }}
                                    >
                                        <Accordion
                                            expanded={expanded.includes(itemIndex) || (!hasHeader)}
                                            onChange={handleAccordionChange(itemIndex)}
                                            sx={{ boxShadow: 'none', '&:before': { display: 'none' }, bgcolor: 'transparent' }}
                                        >
                                            {hasHeader && section.sectionName && (
                                                <AccordionSummary
                                                    expandIcon={<ExpandMoreIcon sx={{ color: accentColor }} />}
                                                    sx={{
                                                        cursor: selectedItem?.isSubmit ? 'default' : 'pointer',
                                                        px: 2.5,
                                                        py: 0.5,
                                                        borderBottom: expanded.includes(itemIndex) ? '1px solid #F0F2F5' : 'none',
                                                        '&:hover': { bgcolor: `${accentColor}06` },
                                                    }}
                                                >
                                                    <Box>
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1A1D23', letterSpacing: '-0.01em' }}
                                                        >
                                                            {section.sectionName}
                                                        </Typography>
                                                        {section.sectionDescription && (
                                                            <Typography variant="body2" sx={{ color: '#9499A8', fontSize: '0.78rem' }}>
                                                                {section.sectionDescription}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </AccordionSummary>
                                            )}
                                            <AccordionDetails sx={{ p: 2.5 }}>
                                                {section?.questions?.length > 0 ? (
                                                    <Stack spacing={1.5}>
                                                        {section.questions.map((question) => renderQuestion(question))}
                                                    </Stack>
                                                ) : (
                                                    <Typography sx={{ textAlign: "center", color: '#9499A8', fontSize: '0.875rem', py: 2 }}>
                                                        {TEXT_NO_QUESTIONS_AVAILABLE}
                                                    </Typography>
                                                )}
                                            </AccordionDetails>
                                        </Accordion>
                                    </Card>
                                );
                            })}
                        </Stack>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                            <Typography sx={{ color: '#9499A8', fontSize: '0.875rem' }}>
                                {TEXT_NO_SEC_NO_QUE_AVAILABLE}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>

                {/* Footer */}
                {!selectedItem?.isSubmit && questionList?.length > 0 && (
                    <Box
                        sx={{
                            px: 3, py: 2,
                            borderTop: '1px solid #F0F2F5',
                            bgcolor: '#fff',
                            display: 'flex',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Button
                            variant="contained"
                            disabled={isInspectionSaving}
                            onClick={() => handleSaveClick('Submit')}
                            startIcon={isInspectionSaving ? <CircularProgress size={15} sx={{ color: 'white' }} /> : null}
                            sx={{
                                bgcolor: isInspectionSaving ? '#E5E7EB' : accentColor,
                                color: isInspectionSaving ? '#9CA3AF' : 'white',
                                borderRadius: '12px',
                                px: 4,
                                py: 1.2,
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                textTransform: 'none',
                                boxShadow: isInspectionSaving ? 'none' : `0 4px 14px ${accentColor}44`,
                                '&:hover': { bgcolor: accentColor, opacity: 0.9 },
                                transition: 'all 0.2s',
                                minWidth: 130,
                            }}
                        >
                            {TEXT_SUBMIT}
                        </Button>
                    </Box>
                )}
            </Dialog>

            {openJobList && activeJobQuestionId && (
                <JobList
                    open={openJobList}
                    onClose={() => setOpenJobList(false)}
                    onSelect={(jobs) => updateAnswer(activeJobQuestionId, jobs)}
                    selectedJobs={answers[activeJobQuestionId]?.value || []}
                />
            )}

            {openSaveDraftModal && (
                <SaveDraftModal
                    open={openSaveDraftModal}
                    onClose={() => setOpenSaveDraftModal(false)}
                    onDiscard={() => { setOpenSaveDraftModal(false); onClose(); }}
                    onSaveDraft={() => { handleSaveClick('Draft'); setOpenSaveDraftModal(false); }}
                />
            )}

            {pictureView && (
                <PictureViewer
                    isOpen={pictureView}
                    onClose={() => setPictureView(false)}
                    pictures={selectedImages.map((src) => ({ src }))}
                    viewerIndex={viewerIndex}
                />
            )}

            {/* Saving overlay */}
            {isInspectionSaving && (
                <Box sx={{ position: 'fixed', inset: 0, zIndex: 9999, bgcolor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ bgcolor: '#fff', borderRadius: '20px', px: 5, py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                        <CircularProgress size={40} sx={{ color: accentColor }} />
                        <Typography sx={{ color: '#1A1D23', fontWeight: 600, fontSize: '0.9rem' }}>Saving…</Typography>
                    </Box>
                </Box>
            )}

            {/* Error dialog */}
            {errorMessage && (
                <Dialog open={!!errorMessage} onClose={() => setErrorMessage(null)}
                    PaperProps={{ sx: { borderRadius: '16px', px: 1, pb: 1 } }}
                >
                    <DialogTitle sx={{ fontWeight: 700, color: '#1A1D23', fontSize: '1rem' }}>Error</DialogTitle>
                    <DialogContent>
                        <Typography sx={{ color: '#6B7280', fontSize: '0.875rem' }}>{errorMessage}</Typography>
                    </DialogContent>
                    <Box sx={{ px: 2, pb: 2 }}>
                        <Button fullWidth variant="contained" onClick={() => setErrorMessage(null)}
                            sx={{ bgcolor: accentColor, borderRadius: '10px', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: accentColor, opacity: 0.9 } }}
                        >
                            OK
                        </Button>
                    </Box>
                </Dialog>
            )}
        </>
    );
};
